/* @pareto-engineering/generator-front 1.0.12 */
import React, {
  useState,
  useMemo,
  useCallback,
  useRef,
} from 'react'

import {
  useHistory,
} from 'react-router-dom'

import styleNames from '@pareto-engineering/bem/exports'

import {
  AnimatedGradient,
  Button,
  Card,
  Label,
  Page,
  Popover,
  useApp,
} from '@pareto-engineering/design-system'

import {
  SectionHero,
  SectionCTA,
  Customers,
  Steps,
  SolutionCard,
  TestimonialCard,
  Footer,
  NavigationBar,
  ConversionPage,
  commonSectionMap,
  InteractiveMap,
} from 'modules/components'

import {
  userTestimonials,
  customerLogos,
  locations,
} from 'modules/common/content'

import {
  SITE,
} from 'modules/urls'

import { OPS_SOLUTION_PATHS } from 'modules/solutions/solutionUrls'

import {
  opsSolutions,
  spadeMap,
} from './content/Home'

const getFutureDate = () => {
  const deliveryDate = new Date(Date.now())
  let businessDays = 0
  while (businessDays < 2) {
    deliveryDate.setDate(deliveryDate.getDate() + 1)
    // 0: Sunday, 6: Saturday
    if (![0, 6].includes(deliveryDate.getDay())) businessDays += 1
  }
  return deliveryDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
}

const baseClassName = styleNames.base

const componentClassName = 'page-home'

const OpsHome = () => {
  const app = useApp()

  const history = useHistory()

  const [dateTime, setDateTime] = useState(Date.now())
  const testimonials = useMemo(() => userTestimonials.sort(() => Math.random() - 0.5)
    .slice(0, 4), [dateTime])
  const shuffleTestimonials = useCallback(() => {
    setDateTime(Date.now())
    const firstTestimonial = document.getElementById('testimonial-1')
    firstTestimonial?.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'start' })
    const testimonialGroup = document.getElementById('testimonials')
    testimonialGroup?.classList.remove('fade-in')
    // eslint-disable-next-line no-void
    void testimonialGroup?.offsetWidth
    testimonialGroup?.classList.add('fade-in')
  }, [])
  const spadeRef = useRef(null)

  const helmetProps = useMemo(() => ({
    title           :'Expert Virtual Assistants for Business Ops | Pareto',
    meta_description:'Complete manual tasks on-demand, 24/7, with our fully managed, expert-vetted operations teams at unbeatable prices.',
    canonical       :SITE.OPS_HOME,
    og_type         :'website',
    main_image      :`${app.SITE.CANONICAL}/misc/social-share.jpg`,
  }), [])

  return (
    <ConversionPage
      conversionFormConfig={commonSectionMap}
      mobileButtonText="Get started"
      desktopLaunchText="Send task instructions and get work done today."
      mobileLaunchText="Ready to complete your tasks?"
      id="page-home"
      helmetProps={helmetProps}
      Background={AnimatedGradient}
      className={[
        baseClassName,
        componentClassName,
      ]
        .filter((e) => e)
        .join(' ')}
    >
      <NavigationBar />
      <SectionHero
        className="to-plain"
        heading="Superhuman assistants for all your business operations"
        subtitle="From lead generation and data collection to virtual assistance, complete manual tasks on-demand, 24/7, with our fully managed, expert-vetted operations teams at unbeatable prices."
        conversionButtonText="Get started"
      />

      <Page.Section
        id="customers"
        className="x-background-far b-x"
      >
        <Customers
          className="span-8 md-start-2 md-span-12 x-highlighted"
          items={customerLogos}
        />
      </Page.Section>

      <Page.Section
        id="main"
        className="x-background-far b-x"
        isSpaced
      >
        <div className="span-8 md-start-6 md-span-7 s2">
          <p>
            We believe in the power of
            {' '}
            <span className="x-main text-gradient">
              focused work.
            </span>
          </p>
          <p>
            Combining top teams of
            {' '}
            <Label isCompact color="interactive">
              data experts
            </Label>
            {' '}
            with AI-driven workflows, we tackle the manual process behind hundreds of
            businesses to help accelerate growth and unlock efficiency.
          </p>
          <p>
            Just send us instructions over Slack or email to start a new project.
            Get quality guaranteed results by
            {' '}
            <Label isCompact color="interactive">
              {getFutureDate()}
            </Label>
            .
          </p>
          <p>
            Focus on what you do best with an
            {' '}
            <div id="spade-interaction" ref={spadeRef}>
              <Label
                className="spade-icon"
                isCompact
                color="interactive"
              >
                &#9824;
              </Label>
              <Popover
                className="s-2"
                parentRef={spadeRef}
                preferredSecondaryPosition={
                  ['right', 'left', 'bottom', 'top']
                }
              >
                <Card>
                  <Card.Section>
                    <ul>
                      {
                        spadeMap.map((item) => (
                          <li>
                            {item}
                          </li>
                        ))
                      }
                    </ul>
                  </Card.Section>
                </Card>
              </Popover>
            </div>
            {' '}
            up your sleeve.
          </p>
        </div>
      </Page.Section>

      <Page.Section
        id="solutions"
        className="x-background-far b-x"
        isSpaced
      >
        <div className="span-8 md-start-2 md-span-6 grid">
          <p className="h1 span-grid remove-margin">
            <span className="x-highlighted c-x">
              Explore popular solutions
            </span>
          </p>
          <p className="span-grid s2">
            Partner with a Pareto project manager to refine instructions,
            design AI-driven workflows, enjoy fast, cost-effective execution, whatever the task.
          </p>
        </div>
        <SolutionCard.Group
          className="span-8 md-start-2 md-span-12"
          desktopType="grid"
          type="snap-scroller"
        >
          {
            opsSolutions.map((solution) => (
              <SolutionCard
                className="span-7 manual-mobile-only md-span-4"
                arrowPosition="bottom"
                style={{ height: '100%' }}
                {...solution}
              />
            ))
          }
        </SolutionCard.Group>
        <div
          className="md-start-7 md-span-2 span-8"
          style={{
            display       :'flex',
            justifyContent:'center',
          }}
        >
          <Button
            color="interactive"
            isGhost
            onClick={() => history.push(OPS_SOLUTION_PATHS.LIST)}
          >
            Explore ops solutions
          </Button>
        </div>
      </Page.Section>

      <Page.Section
        id="unbeatable-rates"
        className="x-background-far b-x"
        isSpaced
      >
        <p className="span-8 md-start-2 md-span-3 x-paragraph s1 c-x">
          Transparent & unbeatable rates
        </p>
        <div className="span-8 md-start-6 md-span-6 grid">
          <p className="h1 span-grid remove-margin">
            <span className="x-highlighted c-x">
              On demand and cost-effective pricing
            </span>
          </p>
          <p className="span-grid s2">
            Our monthly plans start at $20 per hour for 20 hours of work.
            Send manual tasks, set hourly budgets, and pay as you go for additional time.
            Enjoy reduced hourly rates when you upgrade to larger plans.
          </p>
        </div>
      </Page.Section>

      <Page.Section
        id="steps"
        className="x-background-far b-x"
      >
        <p className="span-8 md-start-2 md-span-3 h1 remove-top-margin">
          <span className="x-highlighted c-x">
            How it works
          </span>
        </p>
        <Steps
          items={[
            {
              title      :'Describe your project',
              description:'Our 24/7 team checks your instructions, asks questions to clarify details, and identifies blockers to save you time on costly iterations.',
            },
            {
              title      :'Start tasks in 24 hours',
              description:'Review our cost estimate, set your hourly budget, and see initial results same-day as we match your tasks to experts in our vetted network.',
            },
            {
              title      :'Fully managed & quality assured',
              description:'We guarantee high-quality work with expert review and quality assurance across every project. Enjoy peace of mind at unbeatable prices.',
            },
          ]}
          className="span-8 md-start-6 md-span-6"
        />
      </Page.Section>

      <Page.Section
        id="crowdwork"
        className="x-background-far b-x"
        isSpaced
      >
        <p className="span-8 md-start-2 md-span-3 x-paragraph s1 c-x">
          Redefining ethical crowd work
        </p>
        <div className="span-8 md-start-6 md-span-6 grid">
          <p className="h1 span-grid remove-margin">
            <span className="x-highlighted c-x">
              Built by and for underrepresented talent
            </span>
          </p>
          <p className="span-grid s2">
            The infrastructure behind virtual assistance is outdated. We&apos;ve
            joined forces with seasoned experts and researchers to build a top-tier
            community of expert-vetted, diverse professionals around the world.
          </p>
          <p className="span-grid remove-top-margin s2">
            Pareto operates on the principles of equitable compensation, collaboration,
            and supportive management. Our mission is to
            {' '}
            <span className="x-highlighted c-x">
              empower women and overlooked talent to build careers in technology.
            </span>
          </p>
        </div>
      </Page.Section>

      <Page.Section
        id="map"
        className="x-background-far b-x"
      >
        <InteractiveMap
          color="metadata"
          className="span-grid"
          locations={locations}
        />
      </Page.Section>

      <Page.Section
        id="testimonials"
        className="x-background-far b-x"
        isSpaced
      >
        <p className="h1 md-start-2 md-span-8 span-8">
          <span className="x-highlighted c-x">
            Experience our impact
          </span>
        </p>
        <TestimonialCard.Group
          id="testimonials"
          className="span-8 md-start-2 md-span-12"
          desktopType="grid"
          type="snap-scroller"
        >
          {
            testimonials.map((testimonial, i) => (
              <TestimonialCard
                className="span-7 md-span-6 manual-mobile-only"
                id={`testimonial-${i + 1}`}
                {...testimonial}
              />
            ))
          }
        </TestimonialCard.Group>
        <div
          className="span-8 md-start-2 md-span-12 centered-column"
        >
          <Button
            isGhost
            color="interactive"
            onClick={() => shuffleTestimonials()}
          >
            Load More
          </Button>
        </div>
      </Page.Section>

      <SectionCTA
        className="from-plain"
        heading={
          (
            <span className="x-highlighted c-x">
              Multiply the impact of your time and start sending projects today.
            </span>
          )
        }
        subtitle="Start tasks in 24 hours, starting at $20 per hour."
        buttonText="Get started"
      />
      <Footer />
    </ConversionPage>
  )
}

export default OpsHome
